import { PanelLeft, Layers } from "lucide-react"
import type { ReactNode } from "react"
import { cn } from "@/lib/utils"
import { Button } from "../Button/Button"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "../Tooltip/Tooltip"

export interface SidebarProps {
  collapsed?: boolean
  onToggle?: () => void
  onHomeClick?: () => void
  brandLogo?: ReactNode
  brandMark?: ReactNode
  strategyList?: ReactNode
  footer?: ReactNode
  className?: string
  collapsedStrategiesLabel?: string
}

export function Sidebar({
  collapsed = false,
  onToggle,
  onHomeClick,
  brandLogo,
  brandMark,
  strategyList,
  footer,
  className,
  collapsedStrategiesLabel = "My Strategies",
}: SidebarProps) {
  return (
    <div
      className={cn(
        "group/sidebar relative h-full shrink-0 transition-[width] duration-200",
        collapsed ? "w-14" : "w-52",
        className,
      )}
    >
      <aside className="absolute inset-0 h-full flex flex-col bg-background overflow-hidden border-r border-border">
        <div
          className={cn(
            "h-11 shrink-0 flex items-center border-b border-border",
            collapsed ? "justify-center" : "justify-between px-3",
          )}
        >
          {collapsed ? (
            <div className="relative h-7 w-7">
              <button
                type="button"
                onClick={onHomeClick}
                className="absolute inset-0 flex items-center justify-center no-underline transition-opacity duration-200 ease-out opacity-100 group-hover/sidebar:opacity-0 group-hover/sidebar:pointer-events-none cursor-pointer"
                title="Home"
              >
                {brandMark}
              </button>
              {onToggle && (
                <button
                  type="button"
                  onClick={onToggle}
                  title="Expand sidebar"
                  aria-label="Expand sidebar"
                  className="absolute inset-0 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-opacity duration-200 ease-out opacity-0 pointer-events-none group-hover/sidebar:opacity-100 group-hover/sidebar:pointer-events-auto"
                >
                  <PanelLeft size={15} strokeWidth={1.5} />
                </button>
              )}
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={onHomeClick}
                className="no-underline flex items-center cursor-pointer"
              >
                {brandLogo}
              </button>
              {onToggle && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onToggle}
                  title="Collapse sidebar"
                  aria-label="Collapse sidebar"
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground hover:bg-muted"
                >
                  <PanelLeft size={15} strokeWidth={1.5} />
                </Button>
              )}
            </>
          )}
        </div>

        {collapsed ? (
          <TooltipProvider delayDuration={300}>
            <div className="flex flex-col items-center pt-1 gap-1 flex-1 min-h-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onToggle}
                    aria-label={collapsedStrategiesLabel}
                    className="w-8 h-8 p-0 text-muted-foreground hover:text-foreground hover:bg-muted"
                  >
                    <Layers size={14} strokeWidth={1.5} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">{collapsedStrategiesLabel}</TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        ) : (
          strategyList
        )}

        {footer}
      </aside>
    </div>
  )
}
